import { cx } from '@apparel/react/cx';
import { useAuth } from '@apparel/react/hooks/useAuth';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CartButton } from './CartButton';
import { CartContent } from './CartContent';
import { CartEmitter, CartEvents } from './CartEmitter';

interface CartDropdownProps {
    classes?: {
        button?: any;
    };

    ButtonProps?: Record<string, any>;
}

const useStyles = makeStyles(() => ({
    root: {
        maxWidth: 480,
        width: '80%',
        outline: 'none !important',
    },
}));

export const CartDropdown = React.memo(({ classes, ButtonProps }: CartDropdownProps) => {
    const { checking, auth, user } = useAuth();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const buttonRef = useRef<any>(null);

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [setAnchorEl],
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    useEffect(() => {
        const off = CartEmitter.on(CartEvents.DROPDOWN_SHOW, (state: boolean) => {
            if (state) {
                setAnchorEl(buttonRef.current ?? true);
            } else {
                setAnchorEl(null);
            }
        });

        return () => {
            off();
        };
    }, []);

    if (!checking && auth && user?.user_view !== 'customer') {
        return null;
    }

    return (
        <>
            <CartButton
                ButtonProps={ButtonProps}
                loading={checking}
                onClick={handleClick}
                ref={buttonRef}
                className={classes?.button}
            />
            <Popover
                id={'cart-dropdown'}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{ component: CartContentPaper }}
            />
        </>
    );
});

CartDropdown.displayName = 'CartDropdown';

const CartContentPaper = React.memo(
    React.forwardRef(({ ...rest }: any, ref) => {
        const classes = useStyles();

        rest.className = cx(rest.className, classes.root);

        return (
            <div {...rest} ref={ref as any}>
                <CartContent />
            </div>
        );
    }),
);

export default CartDropdown;
